import { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { useAuth, type StaffRole } from './useAuth';
import { hatMindestensRang } from '@sizilianer/core/features/auth/roles';

/**
 * A-002: Spinner mit Fail-safe. Falls ein Auth-Vorab-Check trotz Timeout
 * hängen sollte, erscheint nach 10 s eine "Neu laden"-Option statt eines
 * unbegrenzten Spinners.
 */
function AuthLoadingGate() {
  const [slow, setSlow] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setSlow(true), 10000);
    return () => clearTimeout(t);
  }, []);
  return (
    <div className="min-h-screen flex flex-col items-center justify-center gap-4 bg-paper px-6 text-center">
      <Loader2 className="size-8 animate-spin text-gold" />
      {slow && (
        <div className="max-w-sm">
          <p className="font-sans text-sm text-ink-soft">
            Das dauert länger als erwartet.
          </p>
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="mt-3 inline-flex items-center gap-1.5 px-4 py-2 bg-gold-2 text-paper rounded text-xs font-semibold uppercase tracking-[0.18em] hover:bg-ink-2"
          >
            Neu laden
          </button>
        </div>
      )}
    </div>
  );
}

type RequireAuthProps = {
  children: React.ReactNode;
  /** Optional minimal-rolle. Standard: jede aktive staff_profile-Rolle reicht */
  minRole?: StaffRole;
  /** Wenn true, ist die Route nur für is_service_admin (Super-Admin) erreichbar. */
  requireServiceAdmin?: boolean;
  /** Wenn true, darf auch die Rolle 'chatbot' (Bot-Betreiber, 0067) auf die
   *  Route — sie steht außerhalb der Rang-Hierarchie und wird sonst immer
   *  nach /admin/chatbot umgeleitet. Gehört an /admin/chatbot selbst plus
   *  die Auth-Hilfsrouten (passwort-setzen, 2fa). */
  allowChatbot?: boolean;
  /** Wenn true, darf auch die Rolle 'ape_mitarbeiter' (Ape-Aushilfe, Runde 5)
   *  auf die Route. Sie steht wie 'chatbot' außerhalb der Rang-Hierarchie und
   *  wird sonst immer nach /admin/ape-verkauf umgeleitet. Gehört an die drei
   *  Ape-Verkauf-Flächen plus die Auth-Hilfsrouten (passwort-setzen, 2fa) —
   *  ohne die letzten beiden liefe eine frisch eingeladene Aushilfe in eine
   *  Umleitungsschleife (Pflicht-Passwortwechsel, B-4). */
  allowApeMitarbeiter?: boolean;
  /** Wenn true, wird die MFA-Challenge übersprungen (z. B. für die Challenge-Page selbst) */
  skipMfaCheck?: boolean;
  /** Wenn true, wird der Onboarding-Auto-Redirect übersprungen (z. B. für /admin/onboarding selbst) */
  skipOnboardingCheck?: boolean;
  /** Wenn true, wird der Pflicht-Passwortwechsel übersprungen (für /admin/passwort-setzen selbst) */
  skipPasswordCheck?: boolean;
};

/**
 * Pure Rollen-Entscheidung (unit-getestet in tests/unit/roleGate.test.ts).
 *
 * WICHTIG: Der Rang-Fallback `?? 0` ist ein Sicherheitsfix — vorher war
 * `hierarchy[unbekannteRolle]` undefined, und `undefined < n` ist false,
 * d. h. JEDE minRole-Route stand einer unbekannten Rolle offen (fail-open).
 */
export function roleGateDecision(opts: {
  role: StaffRole | null;
  isServiceAdmin: boolean;
  minRole?: StaffRole;
  requireServiceAdmin?: boolean;
  allowChatbot?: boolean;
  allowApeMitarbeiter?: boolean;
}): 'ok' | 'redirect_chatbot_home' | 'redirect_ape_home' | 'redirect_dashboard' {
  const { role, isServiceAdmin, minRole, requireServiceAdmin, allowChatbot, allowApeMitarbeiter } =
    opts;

  // Bot-Betreiber: reine Allowlist, Rang-Hierarchie gilt nicht.
  // requireServiceAdmin schlägt die Allowlist (falls je kombiniert).
  if (role === 'chatbot' && !isServiceAdmin) {
    return allowChatbot && !requireServiceAdmin ? 'ok' : 'redirect_chatbot_home';
  }

  // Ape-Aushilfe (Runde 5, E-47): dieselbe Allowlist. Ohne Flag geht es zurück
  // auf ihre Verkaufsliste — NICHT aufs Dashboard: das trägt kein Flag, und die
  // Umleitung dorthin endete in einer Schleife.
  if (role === 'ape_mitarbeiter' && !isServiceAdmin) {
    return allowApeMitarbeiter && !requireServiceAdmin ? 'ok' : 'redirect_ape_home';
  }

  if (minRole) {
    // Bis 05.08.2026 stand hier eine zweite, wörtliche Kopie der
    // Rangtabelle. Zwei Kopien einer Rangordnung driften irgendwann
    // auseinander — dann entscheidet die Sidebar anders als dieser
    // Wächter, und niemand merkt es. Jetzt beide aus core.
    if (!isServiceAdmin && !hatMindestensRang(role, minRole)) {
      return 'redirect_dashboard';
    }
  }

  if (requireServiceAdmin && !isServiceAdmin) {
    return 'redirect_dashboard';
  }

  return 'ok';
}

/**
 * Route-Guard: prüft auf Auth + aktives staff_profile.
 *
 * Item-1-Fix (Bug-Sprint): liest MFA + Onboarding-State aus AuthContext
 * statt eigene useEffects pro Mount. Behebt den Lade-Loop bei Nav-Wechsel.
 *
 * Redirects:
 *  - Loading → Spinner
 *  - Nicht eingeloggt → /admin/login mit return-Pfad
 *  - Eingeloggt aber kein staff_profile → /admin/login mit error
 *  - Inactive profile → /admin/login mit error
 *  - MFA challenge required → /admin/2fa
 *  - Onboarding required → /admin/onboarding
 *  - minRole nicht erfüllt → /admin (Dashboard) mit error
 */
export function RequireAuth({
  children,
  minRole,
  requireServiceAdmin,
  allowChatbot,
  allowApeMitarbeiter,
  skipMfaCheck,
  skipOnboardingCheck,
  skipPasswordCheck,
}: RequireAuthProps) {
  const {
    loading,
    user,
    profile,
    profileReady,
    mfaReady,
    onboardingReady,
    role,
    isServiceAdmin,
    mfaState,
    onboardingState,
    passwordState,
  } = useAuth();
  const location = useLocation();

  // Spinner während der initiale Auth-State noch nicht steht.
  // Hinweis: mfaState/onboardingState starten auf 'checking' — sobald die
  // Provider-Checks fertig sind (1× pro Login, nicht pro Mount!), sind sie
  // 'ok' oder 'redirect_required'.
  // profilePending: beim frischen Login setzt der AuthProvider user synchron,
  // lädt das Profil aber deferred — ohne dieses Gate fiele der Guard in die
  // „kein Profil"-Abweisung und toastete bei JEDEM Login kurz rot. Bewusst
  // OHNE Skip-Flag: das Profil ist Voraussetzung für alle Checks dahinter.
  const profilePending = !!user && !profileReady;
  // C4.4: zusätzlich zu 'checking' auch „Check gehört noch zum vorigen User" —
  // sonst blitzte nach dem Login kurz das Dashboard auf, bevor der 2FA-/
  // Onboarding-Redirect griff (stale 'ok' aus dem anonymen Zustand).
  const mfaPending = !skipMfaCheck && !!user && (mfaState === 'checking' || !mfaReady);
  const onboardingPending =
    !skipOnboardingCheck && !!user && (onboardingState === 'checking' || !onboardingReady);

  if (loading || profilePending || mfaPending || onboardingPending) {
    return <AuthLoadingGate />;
  }

  if (!user) {
    return (
      <Navigate
        to="/admin/login"
        state={{ from: location.pathname + location.search }}
        replace
      />
    );
  }

  if (!profile || !profile.active) {
    return (
      <Navigate
        to="/admin/login"
        state={{
          error: !profile
            ? 'Kein Mitarbeiter-Profil verknüpft.'
            : 'Dein Mitarbeiter-Profil ist deaktiviert.',
        }}
        replace
      />
    );
  }

  // MFA-Challenge nötig — auf Challenge-Page redirecten
  if (!skipMfaCheck && mfaState === 'challenge_required') {
    return (
      <Navigate
        to="/admin/2fa"
        state={{ from: location.pathname + location.search }}
        replace
      />
    );
  }

  // Pflicht-Passwortwechsel (Initialpasswort, 0055) — VOR dem Onboarding:
  // ein frisch angelegter Admin soll erst sein eigenes Passwort setzen, dann
  // in den Wizard. Rein aus dem Profil abgeleitet, PIN-Nutzer sind ausgenommen.
  if (!skipPasswordCheck && passwordState === 'change_required') {
    return (
      <Navigate
        to="/admin/passwort-setzen"
        state={{ from: location.pathname + location.search }}
        replace
      />
    );
  }

  // Onboarding nicht abgeschlossen + Admin (nicht Service-Admin) → Wizard
  if (!skipOnboardingCheck && onboardingState === 'redirect_required') {
    return (
      <Navigate
        to="/admin/onboarding"
        state={{ from: location.pathname + location.search }}
        replace
      />
    );
  }

  const gate = roleGateDecision({
    role,
    isServiceAdmin,
    minRole,
    requireServiceAdmin,
    allowChatbot,
    allowApeMitarbeiter,
  });

  // Bot-Betreiber (0067): sieht nur allowChatbot-Routen; alles andere
  // landet auf seiner einzigen Seite statt auf dem Dashboard.
  if (gate === 'redirect_chatbot_home') {
    return <Navigate to="/admin/chatbot" replace />;
  }

  // Ape-Aushilfe (Runde 5): dasselbe mit ihrer Verkaufsliste.
  if (gate === 'redirect_ape_home') {
    return <Navigate to="/admin/ape-verkauf" replace />;
  }

  if (gate === 'redirect_dashboard') {
    return (
      <Navigate
        to="/admin"
        state={{
          error: requireServiceAdmin && !minRole
            ? 'Dieser Bereich ist nur für Service-Admins.'
            : `Du brauchst mindestens die Rolle "${minRole ?? 'admin'}" für diese Seite.`,
        }}
        replace
      />
    );
  }

  return <>{children}</>;
}
